import * as React from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Ref, useState } from 'react';
import { TinyMCECommonOptions } from 'jamespot-user-api';
import { JRCTinyMCEImageUploadModal } from './JRCTinyMCEImageUploadModal';
import { JRCTinyMCEVideoUploadModal } from './JRCTinyMCEVideoUploadModal';
import { DataCy } from '../../../../types/dataAttributes';

import { JRCTinyMCEFileUploadModal } from './JRCTinyMCEFileUploadModal';
import { TinyMCEExtension } from './extensions/JTinyMCEExtensions';
import { JRCHtml } from '../../../JRCHtml/JRCHtml';
import styled from 'styled-components';

declare global {
    const tinymce: any;
}

const ReadOnlyWrapper = styled.div`
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.color.grey1};
    border-radius: 6px;
    min-height: 40px;
`;

export type TinyMention = {
    type: 'autocompleteitem';
    value: string;
    text: string;
    icon?: string;
};

export type MentionQuery = (query: string) => Promise<TinyMention[]>;

export type JRCInputTinyMCERawProps = DataCy & {
    token?: string;
    commonOptions: TinyMCECommonOptions;
    additionalExtensions?: Array<TinyMCEExtension>;
    value: string | { html: string; text: string };
    onBlur?: () => void;
    onChange: (html: string, text: string) => void;
    ref?: Ref<Editor>;
    tinymceScriptSrc?: string;
    autoFocus?: boolean;
    inline?: boolean;
    placeholder?: string;
    mentionsQueries: {
        users: MentionQuery;
        tags: MentionQuery;
        contents: MentionQuery;
    };
    height?: number;
    autofocus?: boolean;
    readOnly?: boolean;
};
const JRCInputTinyMCERawElement = (
    {
        dataCy,
        token,
        value,
        onBlur,
        onChange,
        mentionsQueries,
        tinymceScriptSrc,
        autoFocus,
        inline = false,
        placeholder,
        additionalExtensions,
        height,
        autofocus,
        ...props
    }: JRCInputTinyMCERawProps,
    ref: React.ForwardedRef<Editor>,
) => {
    const [isImagePopupOpen, setImagePopupOpen] = useState(false);
    const [isVideoPopupOpen, setVideoPopupOpen] = useState(false);
    const [isFilePopupOpen, setFilePopupOpen] = useState(false);
    const [id, setId] = React.useState<undefined | string>(undefined);
    const { license_key, ...options } = props.commonOptions;

    const filePickerCallback = (_cb: unknown, _v: unknown, media: Record<string, any>) => {
        if (media.filetype === 'image') {
            setImagePopupOpen(true);
        }
        if (media.filetype === 'media') {
            setVideoPopupOpen(true);
        }
        if (media.filetype === 'file') {
            setFilePopupOpen(true);
        }
    };

    React.useEffect(() => {
        // Take care of Joséphine
        if (id) {
            autoFocus ? document.getElementById(id)?.focus() : document.getElementById(id)?.blur();
        }
    }, [autoFocus, id]);

    if (props.readOnly) {
        return (
            <ReadOnlyWrapper>
                <JRCHtml
                    __html={
                        !value
                            ? ''
                            : typeof value === 'string'
                            ? value
                            : typeof value === 'object' && 'html' in value
                            ? value.html
                            : ''
                    }
                />
            </ReadOnlyWrapper>
        );
    }

    return (
        <div data-cy={dataCy}>
            <Editor
                inline={inline}
                tinymceScriptSrc={tinymceScriptSrc ?? '/js/tinymce/tinymce.min.js' + props.commonOptions.cache_suffix}
                onInit={(_, editor) => {
                    setId(editor.id);
                }}
                licenseKey={license_key}
                init={{
                    ...options,
                    placeholder,
                    height: height ?? props.commonOptions.height,
                    auto_focus: autofocus || undefined,
                    file_picker_callback: filePickerCallback,
                    setup: (editor) => {
                        additionalExtensions?.forEach((extension) => extension.setup(editor));
                        editor.ui.registry.addAutocompleter('mentions', {
                            trigger: '@',
                            minChars: 1,
                            columns: 1,
                            onAction: (api, range, value) => {
                                editor.selection.setRng(range);
                                editor.insertContent(value);
                                api.hide();
                            },
                            fetch: mentionsQueries.users,
                        });
                        editor.ui.registry.addAutocompleter('tags', {
                            trigger: '#',
                            minChars: 1,
                            columns: 1,
                            onAction: (api, range, value) => {
                                editor.selection.setRng(range);
                                editor.insertContent(value);
                                api.hide();
                            },
                            fetch: mentionsQueries.tags,
                        });
                        editor.ui.registry.addAutocompleter('contents', {
                            trigger: '!',
                            minChars: 1,
                            columns: 1,
                            onAction: (api, range, value) => {
                                editor.selection.setRng(range);
                                editor.insertContent(value);
                                api.hide();
                            },
                            fetch: mentionsQueries.contents,
                        });
                    },
                }}
                onEditorChange={(_, editor) =>
                    onChange(editor.getContent({ format: 'html' }), editor.getContent({ format: 'text' }))
                }
                value={typeof value === 'string' || !value ? value : value.html}
                onBlur={onBlur}
                ref={ref}
            />
            <JRCTinyMCEImageUploadModal
                closeHandler={() => setImagePopupOpen(false)}
                open={isImagePopupOpen}
                token={token}
            />
            <JRCTinyMCEVideoUploadModal
                closeHandler={() => setVideoPopupOpen(false)}
                open={isVideoPopupOpen}
                token={token}
            />
            <JRCTinyMCEFileUploadModal
                closeHandler={() => setFilePopupOpen(false)}
                open={isFilePopupOpen}
                token={token}
            />
        </div>
    );
};

export const JRCInputTinyMCERaw = React.forwardRef(JRCInputTinyMCERawElement);
